import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function ResetPassword({ isOpen, onClose, userId }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: ''
  });

  const checkPasswordStrength = (password) => {
    let score = 0;
    let message = '';

    if (password.length >= 8) score++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) score++;
    if (password.match(/\d/)) score++;
    if (password.match(/[^a-zA-Z\d]/)) score++;

    switch (score) {
      case 0:
        message = 'Very weak';
        break;
      case 1:
        message = 'Weak';
        break;
      case 2:
        message = 'Fair';
        break;
      case 3:
        message = 'Good';
        break;
      case 4:
        message = 'Strong';
        break;
      default:
        message = '';
    }

    return { score, message };
  };

  const getStrengthColor = () => {
    switch (passwordStrength.score) {
      case 1: return 'bg-red-500';
      case 2: return 'bg-yellow-500';
      case 3: return 'bg-blue-500';
      case 4: return 'bg-green-500';
      default: return 'bg-gray-200';
    }
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setStatus('error');
      setErrorMessage('Passwords do not match');
      return;
    }

    if (passwordStrength.score < 3) {
      setStatus('error');
      setErrorMessage('Password is not strong enough');
      return;
    }

    setStatus('loading');
    console.log('loading');
    try {
      const response = await fetch(
        'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/resetpassword',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            userId,
            newPassword: password 
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to reset password');
      }

      setStatus('success');
      console.log('Password reset successful');
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message);
      console.error('Error resetting password:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex justify-center items-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Set New Password</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 bg-gray-50">
          {status === 'success' ? (
            <div className="text-center text-green-600">
              Password updated successfully!
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={handlePasswordChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
                    required
                    minLength={8}
                  />
                  {password && (
                    <div className="mt-1">
                      <div className="h-1 w-full bg-gray-200 rounded">
                        <div 
                          className={`h-1 rounded transition-all ${getStrengthColor()}`}
                          style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-600">
                        Password strength: {passwordStrength.message}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
                    required
                    minLength={8}
                  />
                </div>

                <p className="mt-1 text-xs text-gray-500">
                  Password must contain:
                  <span className="block ml-2">• At least 8 characters</span>
                  <span className="block ml-2">• Upper and lowercase letters</span>
                  <span className="block ml-2">• At least one number</span>
                  <span className="block ml-2">• At least one special character</span>
                </p>

                {status === 'error' && (
                  <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="mt-4 w-full bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800 disabled:bg-slate-400"
                >
                  {status === 'loading' ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
} 